import axios from "axios";
import { useEffect, useState } from "react";
import { Modal, Spinner, Table } from "react-bootstrap";
import { isAuthenticated } from "../../auth/helper/auth";
import { API } from "../../backend";
import { CategoryT, CourseT } from "../../types/model";
import {
  deleteHttpHandler,
  getHttpHandler,
  postHttpHandler,
} from "../../utils/httpHandler";
import { ReactComponent as ManageCourseImg } from "../../assets/manageCourse.svg";
import { ReactComponent as AddImg } from "../../assets/add.svg";
import { ReactComponent as ActivateImg } from "../../assets/activate.svg";
import { ReactComponent as DeActivateImg } from "../../assets/deactivate.svg";
import { ReactComponent as DownloadImg } from "../../assets/download.svg";
import { ReactComponent as DeleteImg } from "../../assets/delete.svg";
import { ReactComponent as EditImg } from "../../assets/edit.svg";
import { ReactComponent as DownArrow } from "../../assets/downArrow.svg";
import { ReactComponent as Error } from "../../assets/error.svg";
import DatePicker from "react-datepicker";
import "./ManageCourse.css";
import "react-datepicker/dist/react-datepicker.css";

interface NewCourseT {
  name: string;
  description: string;
  instructorName: string;
  price: number;
  startDate: Date | null;
  endDate: Date | null;
  category: string;
  thumbnail: File | null;
}

type CourseObjectType = keyof NewCourseT;

const ManageCourse = () => {
  const [courseData, setCourseData] = useState<NewCourseT>({
    name: "",
    description: "",
    instructorName: "",
    price: 0,
    startDate: new Date(),
    endDate: new Date(new Date().setMonth(new Date().getMonth() + 1)),
    category: "",
    thumbnail: null,
  });
  const [courseLoading, setCourseLoading] = useState<boolean>(false);
  const [errorMess, setErrorMess] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingActivate, setIsLoadingActivate] = useState<[string, boolean]>(
    ["", false]
  );
  const [isDeleting, setIsDeleting] = useState<[string, boolean]>(["", false]);
  const [isListOpen, setIsListOpen] = useState<boolean>(false);
  const [selectedCate, setSelectedCate] = useState<string>("");
  const [categories, setCategories] = useState<CategoryT[]>([]);
  const [instructors, setInstructors] = useState<string[]>([]);
  const [insList, setInsList] = useState<string[]>([]);

  const [courses, setCourses] = useState<CourseT[]>([]);
  const [checkedCourses, setCheckedCourses] = useState<string[]>([]);
  const [show, setShow] = useState<boolean>(false);

  const getAllCourses = async () => {
    setCourseLoading(true);
    try {
      const allCourses: CourseT[] = await getHttpHandler({
        url: `${API}/courses`,
      });
      setCourses(allCourses);
      let activeCourses: string[] = [];
      allCourses?.map(
        (course) => course.isActive && activeCourses.push(course._id)
      );
      setCourseLoading(false);
    } catch (error) {
      setCourseLoading(false);
      console.log(error);
    }
  };

  const deleteCourse = async (courseId: string) => {
    setIsDeleting([courseId, true]);
    try {
      const deletingCourseResp = await deleteHttpHandler({
        url: `${API}/course/${courseId}/${isAuthenticated()?.user._id}`,
        idToken: isAuthenticated()?.token,
      });
      getAllCourses();
      setIsDeleting([courseId, false]);
    } catch (errorResp: any) {
      setIsDeleting([courseId, false]);
      console.log(errorResp);
    }
  };

  const updateActive = async (isActive: boolean) => {
    if (!checkedCourses.length) return;
    setIsLoadingActivate([isActive ? "activate" : "deactivate", true]);
    try {
      const updateActiveResp = await postHttpHandler({
        url: `${API}/course/update/active/${isAuthenticated()?.user._id}`,
        idToken: isAuthenticated()?.token,
        body: {
          isActive,
          coursesId: checkedCourses,
        },
      });
      getAllCourses();
      setCheckedCourses([]);
      setIsLoadingActivate([isActive ? "activate" : "deactivate", false]);
    } catch (errorResp: any) {
      setIsLoadingActivate([isActive ? "activate" : "deactivate", false]);
      console.log(errorResp);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    e.preventDefault();
    const target: any = e.target;

    if (target?.type === "file") {
      if (
        target.files?.[0].type !== "image/png" &&
        target.files?.[0].type !== "image/jpeg"
      ) {
        e.target.value = "";
        return alert("File should be of PNG or JPEG format");
      }
      if (target.files?.[0].size > 1000000) {
        e.target.value = "";
        return alert("File size should be less than 1MB");
      }
    }

    if (target.name === "instructorName") {
      autocompleteMatch(target.value);
    }

    setCourseData({
      ...courseData,
      [target.name]:
        target.type === "date"
          ? new Date(target.value)
          : target.type === "file"
          ? target.files?.[0]
          : target.value,
    });
  };

  const onSubmit = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    if (isNaN(courseData.price)) {
      return setErrorMess("Price should be a number!");
    }
    if (!courseData.thumbnail) {
      return setErrorMess("Please select a thumbnail!");
    }
    setIsLoading(true);

    const formData: any = new FormData();
    Object.keys(courseData).map((key) =>
      formData.append(key, courseData[key as CourseObjectType])
    );

    try {
      const addCourseResp = await axios({
        url: `${API}/course/create/${isAuthenticated()?.user._id}`,
        method: "Post",
        headers: { Authorization: `Bearer ${isAuthenticated()?.token}` },
        data: formData,
      });
      if (addCourseResp?.data?._id) {
        getAllCourses();
        setErrorMess("");
        setCourseData({
          name: "",
          description: "",
          instructorName: "",
          price: 0,
          startDate: new Date(),
          endDate: new Date(new Date().setMonth(new Date().getMonth() + 1)),
          category: "",
          thumbnail: null,
        });
        setSelectedCate("");
        setShow(false);
        setIsLoading(false);
      }
      setIsLoading(false);
    } catch (errorResp: any) {
      console.log(errorResp.response);
      setIsLoading(false);
      setErrorMess(errorResp.response.data?.error || "Something went wrong!");
    }
  };

  const preloadCategories = async () => {
    try {
      const allCategories = await getHttpHandler({ url: `${API}/categories` });
      if (allCategories && allCategories.length > 0) {
        setCategories(allCategories);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const preloadInstructors = async () => {
    try {
      const allInstrcutors = await getHttpHandler({
        url: `${API}/courses/instructors`,
      });
      if (allInstrcutors && allInstrcutors.length > 0) {
        setInstructors(allInstrcutors);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getAllCourses();
    preloadCategories();
    preloadInstructors();
  }, []);

  const autocompleteMatch = (input: string) => {
    input = input.toLowerCase();
    if (input === "") {
      return setInsList([]);
    }
    var reg = new RegExp(input);
    let finalInsList = instructors.filter(function (term) {
      if (term.toLocaleLowerCase().match(reg)) {
        return term;
      }
    });
    setInsList(finalInsList);
  };

  return (
    <div className="manage-course">
      <Modal
        show={show}
        onHide={() => {
          setErrorMess("");
          setCourseData({
            name: "",
            description: "",
            instructorName: "",
            price: 0,
            startDate: new Date(),
            endDate: new Date(new Date().setMonth(new Date().getMonth() + 1)),
            category: "",
            thumbnail: null,
          });
          setSelectedCate("");
          setShow(false);
        }}
        className="add-course-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title className="title">Add New Course</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h3 className="label-text">Thumbnail</h3>
          <input
            onChange={handleChange}
            type="file"
            name="thumbnail"
            id="thumbnail"
            accept="image"
          />
          <h3 className="label-text">Course Name</h3>
          <input
            onChange={handleChange}
            name="name"
            id="name"
            value={courseData.name}
            type="text"
          />
          <h3 className="label-text">Description</h3>
          <textarea
            onChange={handleChange}
            name="description"
            id="description"
            value={courseData.description}
          />
          <div className="combine">
            <div className="combine-child">
              <h3 className="label-text">Instructor</h3>
              <input
                onChange={handleChange}
                name="instructorName"
                
                value={courseData.instructorName}
                type="text"
                id="instructorNameId"
              />
              {insList?.length > 0 && (
                <div className="intructorsList">
                  {insList.map((ins: string, index: number) => (
                    <p
                      key={index}
                      onClick={() => {
                        setCourseData({
                          ...courseData,
                          instructorName: ins,
                        });
                        setInsList([]);
                      }}
                    >
                      {ins}
                    </p>
                  ))}
                </div>
              )}
            </div>
            <div className="combine-child">
              <h3 className="label-text">Price</h3>
              <input
                onChange={handleChange}
                name="price"
                id="price"
                value={courseData.price}
                type="text"
              />
            </div>
          </div>
          <div className="combine">
            <div className="combine-child">
              <h3 className="label-text">Starts From</h3>
              <DatePicker
                closeOnScroll={true}
                name="startDate"
                selected={courseData.startDate}
                onChange={(date) =>
                  setCourseData({ ...courseData, startDate: date })
                }
                minDate={new Date()}
              />
            </div>
            <div className="combine-child">
              <h3 className="label-text">Ends On</h3>
              <DatePicker
                closeOnScroll={true}
                name="endDate"
                selected={courseData.endDate}
                onChange={(date) =>
                  setCourseData({ ...courseData, endDate: date })
                }
                minDate={courseData.startDate}
              />
            </div>
          </div>
          <h3 className="label-text">Category</h3>
          <button
            className="menu-btn"
            onClick={(e) => {
              e.preventDefault();
              setIsListOpen(!isListOpen);
            }}
          >
            <div>
              {selectedCate || "Select Category"} <DownArrow />
            </div>
          </button>
          {isListOpen && (
            <div className="menu-items">
              {categories?.map((category) => (
                <button
                  className="menu-item"
                  key={category._id}
                  onClick={(e) => {
                    e.preventDefault();
                    setIsListOpen(false);
                    setSelectedCate(category.name);
                    setCourseData({
                      ...courseData,
                      category: category._id,
                    });
                  }}
                >
                  {category.name}
                </button>
              ))}
            </div>
          )}
          {errorMess && (
            <h2 className="errorMessage">
              <Error className="errorIcon" />
              {errorMess}
            </h2>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            className="action-btn white-action-btn"
            onClick={(e) => {
              e.preventDefault();
              setErrorMess("");
              setCourseData({
                name: "",
                description: "",
                instructorName: "",
                price: 0,
                startDate: new Date(),
                endDate: new Date(
                  new Date().setMonth(new Date().getMonth() + 1)
                ),
                category: "",
                thumbnail: null,
              });
              setSelectedCate("");
              setShow(false);
              setIsLoading(false);
            }}
          >
            Cancel
          </button>
          <button
            className="action-btn"
            onClick={onSubmit}
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : "Save"}
          </button>
        </Modal.Footer>
      </Modal>
      <div className="header-div">
        <h1 className="title">
          <ManageCourseImg className="title-icon" />
          Manage Courses
        </h1>
        <div className="manage-btns">
          <button
            className="white-btn"
            onClick={(e) => {
              e.preventDefault();
              updateActive(true);
            }}
          >
            {isLoadingActivate[1] && isLoadingActivate[0] === "activate"
              ? "Activating..."
              : "Activate Courses"}
            <ActivateImg />
          </button>
          <button
            className="white-btn"
            onClick={(e) => {
              e.preventDefault();
              updateActive(false);
            }}
          >
            {isLoadingActivate[1] && isLoadingActivate[0] === "deactivate"
              ? "Deactivating..."
              : "Deactivate Courses"}{" "}
            <DeActivateImg />
          </button>
          <button
            onClick={(e) => {
              e.preventDefault();
              setShow(true);
            }}
          >
            Add New Course <AddImg />
          </button>
        </div>
      </div>

      {courseLoading ? (
        <Spinner animation="border" />
      ) : (
        <Table responsive borderless className="courses-table">
          <thead>
            <tr>
              <th></th>
              <th>Course Name</th>
              <th>Course Price</th>
              <th>Instructor Name</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th>Active</th>
              <th>Category</th>
              <th>Enroll Count</th>
              <th>Thumbnail</th>
              {/* <th>Download</th> */}
              <th>Delete</th>
              {/* <th>Edit</th> */}
            </tr>
          </thead>
          <tbody>
            {courses.length > 0 &&
              courses?.map((course) => (
                <tr key={course?._id} id={`course_${course?._id}`}>
                  <td>
                    <input
                      id={`course_select_${course?._id}`}
                      type={"checkbox"}
                      checked={checkedCourses.indexOf(course?._id) > -1}
                      onChange={(e) => {
                        e.preventDefault();
                        if (e.target.checked) {
                          setCheckedCourses([...checkedCourses, course?._id]);
                        } else {
                          setCheckedCourses(
                            checkedCourses.filter((id) => id !== course?._id)
                          );
                        }
                      }}
                    />
                  </td>
                  <td>{course?.name}</td>
                  <td>₹{course?.price}</td>
                  <td>{course?.instructorName}</td>
                  <td>{new Date(course?.startDate).toLocaleDateString()}</td>
                  <td>{new Date(course?.endDate).toLocaleDateString()}</td>
                  <td>
                    {course?.isActive ? <ActivateImg /> : <DeActivateImg />}
                  </td>
                  <td>{course?.category?.name || "None"}</td>
                  <td>{course?.enrolledUsers?.length}</td>
                  <td>
                    <img
                      id={`course_thumbnail_${course._id}`}
                      src={`${API}/course/thumbnail/${course._id}`}
                      alt="thumbnail"
                      width={"120px"}
                    />
                  </td>
                  {/* <td>
                    <a
                      href={`${API}/course/thumbnail/${course._id}`}
                      download="thumbnail"
                      onClick={(e: any) => {
                        fetch(e.target.href, {
                          method: "GET",
                          headers: {},
                        })
                          .then((response) => {
                            response.arrayBuffer().then(function (buffer) {
                              const url = window.URL.createObjectURL(
                                new Blob([buffer])
                              );
                              const link = document.createElement("a");
                              link.href = url;
                              link.setAttribute("download", "image.png");
                              document.body.appendChild(link);
                              link.click();
                            });
                          })
                          .catch((err) => {
                            console.log(err);
                          });
                      }}
                    >
                      <DownloadImg />
                    </a>
                  </td> */}
                  <td>
                    <button
                      disabled={isDeleting[0] === course._id && isDeleting[1]}
                      className="action-btn delete-btn"
                      onClick={(e) => {
                        e.preventDefault();
                        deleteCourse(course._id);
                      }}
                    >
                      {isDeleting[0] === course._id && isDeleting[1]
                        ? "Deleting..."
                        : "Delete"}
                      <DeleteImg />
                    </button>
                  </td>
                  {/* <td>
                  <EditImg />
                </td> */}
                </tr>
              ))}
          </tbody>
        </Table>
      )}
    </div>
  );
};

export default ManageCourse;
